import { useEffect, useState } from "react";
import { ArrowRight, Coffee, Leaf, Citrus, Zap, Sparkles, Droplets } from "lucide-react";
import PublicLayout from "@/components/layout/PublicLayout";
import api from "@/lib/api";
import { CONSULTATION_URL } from "@/data/constants";
import useSEO, { breadcrumbSchema } from "@/hooks/useSEO";

// Kind → icon mapping for coming-soon collections
const KIND_ICON = {
  water: Droplets,
  coffee: Coffee,
  tea: Leaf,
  juice: Citrus,
  energy_performance: Zap,
  functional_wellness: Sparkles,
};

const Products = () => {
  useSEO({
    title: "Product Categories — FLOWTALIS™",
    description: "Explore FLOWTALIS™ private-label beverage categories — purified, alkaline, spring, sparkling, CBD water, functional wellness, energy, and more. Standard MOQ 20,000 units.",
    canonical: "https://flowtalis.com/products",
    jsonLd: breadcrumbSchema([
      { label: "Home", href: "/" },
      { label: "Products", href: "/products" },
    ]),
  });
  const [products, setProducts] = useState([]);
  const [collections, setCollections] = useState([]);
  const [allBadges, setAllBadges] = useState([]);
  const [activeFilters, setActiveFilters] = useState([]);
  const [filterMode, setFilterMode] = useState("all"); // 'all' = Match All, 'any' = Match Any
  const [moqStandard, setMoqStandard] = useState("");

  useEffect(() => {
    api.get("/products").then((r) => {
      setProducts(r.data.products || []);
      setMoqStandard(r.data.moq_standard || "");
    }).catch(() => {});
    api.get("/collections").then((r) => {
      setCollections(r.data.collections || []);
    }).catch(() => {});
    api.get("/compliance-badges").then((r) => setAllBadges(r.data.badges || [])).catch(() => {});
  }, []);

  const toggleFilter = (slug) => {
    setActiveFilters((f) => f.includes(slug) ? f.filter((s) => s !== slug) : [...f, slug]);
  };

  // Only surface badges that are actually assigned to at least one product.
  const availableBadgeSlugs = new Set();
  products.forEach((p) => (p.compliance_badges || []).forEach((b) => availableBadgeSlugs.add(b.slug)));
  const availableBadges = allBadges.filter((b) => availableBadgeSlugs.has(b.slug));

  const matchesFilters = (p) => {
    if (activeFilters.length === 0) return true;
    const productSlugs = (p.compliance_badges || []).map((b) => b.slug);
    return filterMode === "any"
      ? activeFilters.some((f) => productSlugs.includes(f))
      : activeFilters.every((f) => productSlugs.includes(f));
  };

  const filteredProducts = products.filter(matchesFilters);

  const activeCollections = collections
    .filter((c) => c.status === "active")
    .sort((a, b) => (a.display_order || 0) - (b.display_order || 0));

  const comingSoon = collections.filter((c) => c.status === "coming_soon")
    .sort((a, b) => (a.display_order || 0) - (b.display_order || 0));

  const productsByCollection = (slug) =>
    filteredProducts.filter((p) => (p.collection_slug || "water") === slug);

  return (
    <PublicLayout>
      {/* HERO */}
      <section className="ft-section-dark py-24" data-testid="products-hero">
        <div className="ft-container ft-fade-in max-w-3xl relative">
          <div className="text-[0.6875rem] uppercase tracking-[0.32em] text-[var(--ft-gold)] font-semibold mb-6">
            Product Collections
          </div>
          <span className="ft-gold-rule-lg" />
          <h1 className="mt-6 text-white text-[clamp(2.25rem,4.5vw,3.5rem)] font-bold leading-[1.02] tracking-tight mb-8">
            Premium private-label<br />
            <span className="ft-serif text-[var(--ft-gold)]">beverage collections</span>, ready for your brand.
          </h1>
          <p className="text-white/60 text-lg leading-relaxed">
            Water and Functional &amp; Wellness collections are live today. Coffee, Tea, Juice, and Energy &amp;
            Performance join the roster next.
          </p>
        </div>
      </section>

      {/* BADGE FILTER BAR — dynamically populated from the compliance-badge catalog */}
      {availableBadges.length > 0 && (
        <section className="py-6 bg-white border-b border-[var(--ft-border)] sticky top-0 z-20" data-testid="badge-filter-bar">
          <div className="ft-container">
            <div className="flex items-center gap-3 flex-wrap">
              <div className="text-[0.6875rem] uppercase tracking-[0.24em] text-[var(--ft-gold)] font-semibold mr-2">Filter by Certification</div>
              {/* Match Any / Match All toggle */}
              <div
                className="inline-flex rounded-full border border-[var(--ft-border)] p-0.5 bg-white"
                role="group"
                aria-label="Certification match mode"
                data-testid="filter-mode-toggle"
              >
                <button
                  type="button"
                  onClick={() => setFilterMode("all")}
                  data-testid="filter-mode-all"
                  aria-pressed={filterMode === "all"}
                  className={`text-[0.65rem] font-semibold tracking-[0.14em] uppercase px-3 py-1 rounded-full transition-all ${
                    filterMode === "all"
                      ? "bg-[var(--ft-black)] text-white"
                      : "text-[var(--ft-muted)] hover:text-[var(--ft-black)]"
                  }`}
                  title="Only show products that carry every selected certification"
                >
                  Match All
                </button>
                <button
                  type="button"
                  onClick={() => setFilterMode("any")}
                  data-testid="filter-mode-any"
                  aria-pressed={filterMode === "any"}
                  className={`text-[0.65rem] font-semibold tracking-[0.14em] uppercase px-3 py-1 rounded-full transition-all ${
                    filterMode === "any"
                      ? "bg-[var(--ft-black)] text-white"
                      : "text-[var(--ft-muted)] hover:text-[var(--ft-black)]"
                  }`}
                  title="Show products that carry any of the selected certifications"
                >
                  Match Any
                </button>
              </div>
              {availableBadges.map((b) => {
                const active = activeFilters.includes(b.slug);
                return (
                  <button
                    key={b.slug}
                    type="button"
                    onClick={() => toggleFilter(b.slug)}
                    data-testid={`filter-${b.slug}`}
                    aria-pressed={active}
                    className={`text-[0.7rem] font-semibold tracking-wider uppercase px-3 py-1.5 rounded-full border transition-all ${
                      active
                        ? "bg-[var(--ft-gold)] text-[var(--ft-black)] border-[var(--ft-gold)]"
                        : "border-[var(--ft-border)] text-[var(--ft-charcoal)] hover:border-[var(--ft-gold)] hover:text-[var(--ft-gold)]"
                    }`}
                  >
                    {b.label}
                  </button>
                );
              })}
              {activeFilters.length > 0 && (
                <button
                  type="button"
                  onClick={() => setActiveFilters([])}
                  data-testid="filter-clear"
                  className="text-[0.7rem] font-semibold tracking-wider uppercase px-3 py-1.5 rounded-full text-[var(--ft-muted)] hover:text-[var(--ft-black)]"
                >
                  Clear
                </button>
              )}
            </div>
          </div>
        </section>
      )}

      {/* ACTIVE COLLECTIONS — one section per collection */}
      {activeCollections.map((col, idx) => {
        const colProducts = productsByCollection(col.slug);
        if (colProducts.length === 0) return null;
        const bg = idx % 2 === 0 ? "bg-white" : "bg-[var(--ft-light-gray)]";
        return (
          <section key={col.slug} className={`py-24 ${bg}`} data-testid={`collection-section-${col.slug}`}>
            <div className="ft-container">
              <div className="max-w-2xl mb-14">
                <div className="ft-eyebrow mb-4">{col.tagline || "Live"}</div>
                <span className="ft-gold-rule-lg" />
                <h2 className="mt-6 text-[clamp(1.875rem,3.5vw,2.75rem)] font-bold leading-[1.05] tracking-tight text-[var(--ft-black)]">
                  {col.name}
                </h2>
                {col.description && (
                  <p className="mt-5 text-[var(--ft-muted)] text-lg leading-relaxed">{col.description}</p>
                )}
              </div>

              <div className="grid md:grid-cols-2 gap-6" data-testid={`products-grid-${col.slug}`}>
                {colProducts.map((p) => (
                  <a
                    key={p.slug}
                    href={CONSULTATION_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="ft-card block group"
                    data-testid={`product-card-${p.slug}`}
                  >
                    <div className="flex items-start justify-between gap-3 flex-wrap">
                      <div className="ft-badge text-[0.6rem] leading-tight">
                        {p.min_order || moqStandard}
                      </div>
                      <div className="text-xs uppercase tracking-widest text-[var(--ft-muted)] shrink-0">
                        {p.timeline}
                      </div>
                    </div>
                    <h3 className="mt-6 text-2xl font-bold tracking-tight text-[var(--ft-black)]">
                      {p.name}
                    </h3>
                    <p className="mt-3 text-[var(--ft-muted)]">{p.tagline}</p>
                    {(p.compliance_badges || []).length > 0 && (
                      <div className="mt-4 flex flex-wrap gap-1.5" data-testid={`badges-${p.slug}`}>
                        {(p.compliance_badges || []).map((b) => (
                          <span
                            key={b.slug}
                            className="inline-flex items-center gap-1 text-[0.625rem] font-semibold tracking-wider uppercase px-2 py-1 rounded-full border border-[var(--ft-gold)] text-[var(--ft-gold)]"
                            title={b.label}
                          >
                            {b.label}
                          </span>
                        ))}
                      </div>
                    )}
                    <div className="mt-6">
                      <div className="text-[0.6875rem] uppercase tracking-[0.24em] text-[var(--ft-gold)] font-semibold mb-2">
                        Available Sizes
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {(p.bottle_options || []).map((b) => (
                          <span
                            key={b}
                            className="text-xs px-3 py-1.5 rounded-full border border-[var(--ft-border)] text-[var(--ft-charcoal)] bg-white font-medium"
                            data-testid={`size-chip-${p.slug}-${b.replace(/\s+/g, "-")}`}
                          >
                            {b}
                          </span>
                        ))}
                      </div>
                    </div>
                    <div className="mt-6 inline-flex items-center gap-2 text-sm font-semibold text-[var(--ft-gold)] group-hover:gap-3 transition-all">
                      Book a Consultation <ArrowRight size={16} />
                    </div>
                  </a>
                ))}
              </div>
            </div>
          </section>
        );
      })}

      {/* COMING SOON COLLECTIONS */}
      {comingSoon.length > 0 && (
        <section className="py-24 bg-[var(--ft-light-gray)]" data-testid="coming-soon-section">
          <div className="ft-container">
            <div className="max-w-2xl mb-14">
              <div className="ft-eyebrow mb-4">Coming Soon</div>
              <span className="ft-gold-rule-lg" />
              <h2 className="mt-6 text-[clamp(1.875rem,3.5vw,2.75rem)] font-bold leading-[1.05] tracking-tight text-[var(--ft-black)]">
                The next <span className="ft-serif font-medium text-[var(--ft-gold)]">collections</span>.
              </h2>
              <p className="mt-5 text-[var(--ft-muted)] text-lg leading-relaxed">
                Additional beverage collections currently in development. Reserve your category
                early by booking a consultation with a FLOWTALIS specialist.
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="coming-soon-grid">
              {comingSoon.map((c) => {
                const Icon = KIND_ICON[c.kind] || Sparkles;
                return (
                  <div
                    key={c.slug}
                    className="relative rounded-2xl overflow-hidden border border-[var(--ft-border)] bg-[var(--ft-black)] aspect-[4/5] group cursor-not-allowed select-none"
                    data-testid={`coming-soon-card-${c.slug}`}
                    aria-disabled="true"
                  >
                    {/* Background image */}
                    {c.hero_image_url && (
                      <img
                        src={c.hero_image_url}
                        alt=""
                        className="absolute inset-0 w-full h-full object-cover opacity-55 group-hover:opacity-60 transition-opacity duration-700"
                        loading="lazy"
                        draggable="false"
                      />
                    )}
                    {/* Luxury gradient overlay */}
                    <div
                      className="absolute inset-0"
                      style={{
                        background:
                          "linear-gradient(180deg, rgba(17,17,17,0.35) 0%, rgba(17,17,17,0.55) 50%, rgba(17,17,17,0.9) 100%)",
                      }}
                    />
                    {/* Subtle gold vignette */}
                    <div
                      className="absolute inset-0 pointer-events-none"
                      style={{
                        background:
                          "radial-gradient(circle at 85% 12%, rgba(200,162,74,0.10), transparent 55%)",
                      }}
                    />

                    {/* Coming Soon Badge */}
                    <div className="absolute top-5 right-5 z-10">
                      <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[0.625rem] font-bold tracking-[0.22em] bg-[var(--ft-gold)] text-[var(--ft-black)] uppercase">
                        {c.badge_text || "Coming Soon"}
                      </span>
                    </div>

                    {/* Content */}
                    <div className="relative z-10 h-full flex flex-col justify-between p-7">
                      {/* Gold circular icon */}
                      <div
                        className="h-14 w-14 rounded-full flex items-center justify-center border border-[var(--ft-gold)]"
                        style={{ background: "rgba(200,162,74,0.10)" }}
                      >
                        <Icon size={22} strokeWidth={1.5} className="text-[var(--ft-gold)]" />
                      </div>

                      <div>
                        <h3 className="text-white text-2xl font-bold tracking-tight leading-tight">
                          {c.name}
                        </h3>
                        <div className="mt-2 mb-3 h-[2px] w-10 bg-[var(--ft-gold)]" />
                        <p className="text-white/70 text-sm leading-relaxed">
                          {c.description}
                        </p>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* CTA — Don't see what you're looking for? */}
      <section className="py-28 bg-white border-t border-[var(--ft-border)]" data-testid="products-cta-section">
        <div className="ft-container text-center max-w-3xl">
          <div className="ft-eyebrow mb-5">Custom Collections</div>
          <span className="ft-gold-rule-lg mx-auto" />
          <h2 className="mt-6 text-[clamp(2rem,4vw,3rem)] font-bold leading-[1.05] tracking-tight text-[var(--ft-black)] mb-6">
            Don&apos;t see what you&apos;re<br />
            <span className="ft-serif font-medium text-[var(--ft-gold)]">looking for?</span>
          </h2>
          <p className="text-[var(--ft-muted)] mb-10 text-lg max-w-xl mx-auto leading-relaxed">
            FLOWTALIS scopes custom beverage collections beyond the categories listed here.
            Book a consultation with a specialist to explore your private-label opportunity.
          </p>
          <a
            href={CONSULTATION_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="ft-btn-gold"
            data-testid="products-cta-book"
          >
            Book a Consultation <ArrowRight size={16} />
          </a>
        </div>
      </section>
    </PublicLayout>
  );
};

export default Products;
